import type { HelmAccess } from '../roles'

/**
 * Guided tours: the rules, with no React and no DOM, so they can be tested on
 * their own. A tour is a list of steps that point at real elements marked
 * `data-tour="…"`. A step moves on when the person presses Next, or when they
 * do what it asks (open the page, move the form on); Esc or Skip ends it.
 * Each person's progress is a small record kept per login.
 */

/** A page a step happens on. */
export interface TourPage {
  /** Matches the pathname. */
  match: RegExp
  /** Where "Go there" leads; a page about one record has none. */
  href?: string
  /** As in "This step is on New Inquiry". */
  label: string
}

/** What the person does that completes a step. */
export type TourAdvance = { page: TourPage } | { target: string }

export interface TourStep {
  id: string
  title: string
  body: string
  /** The `data-tour` name of the element to highlight. None: a card on its own. */
  target?: string
  /** Highlighted instead when the target can't be seen, e.g. the menu button on a phone. */
  fallback?: { target: string; note: string }
  /** Where the step happens, when it is not everywhere. */
  page?: TourPage
  /** Done when this becomes true; Next also moves on. */
  advance?: TourAdvance
  /**
   * What Next does for the person instead of skipping ahead: the same safe
   * action (open the page, press the form's own Next). Never saves anything.
   */
  perform?: { click: string } | { navigate: string }
  /** Said when the target isn't on screen, e.g. "Open a parcel first." */
  missing?: string
  /** Where the card prefers to sit beside the target. */
  side?: CardSide
  /** A way in (open the menu, open the list), skipped when someone starts further in. */
  lead?: boolean
  /** Only for some people. */
  when?: (access: HelmAccess) => boolean
}

export interface TourDefinition {
  id: string
  title: string
  summary: string
  audience: (access: HelmAccess) => boolean
  steps: readonly TourStep[]
}

export interface TourRecord {
  completedAt?: string
  skippedAt?: string
  /** The step someone last saw, for Resume. */
  lastStep?: number
}

export interface TourProgress {
  version: 1
  /** When Getting started was first offered, so it is offered once. */
  offeredAt: string | null
  tours: Record<string, TourRecord>
}

export interface ActiveTour {
  tourId: string
  index: number
  /**
   * Whether the step is waiting for its `advance` to become true. Unknown until
   * the first look; false while it was already true, so a step that is already
   * done does not skip itself and Back does not bounce.
   */
  armed: boolean | null
}

export interface OnboardingState {
  active: ActiveTour | null
  progress: TourProgress
}

export type OnboardingEvent =
  | { type: 'START'; tourId: string; index?: number; at: string }
  | { type: 'NEXT'; at: string }
  | { type: 'BACK' }
  | { type: 'SKIP'; at: string }
  | { type: 'OBSERVE'; stepId: string; satisfied: boolean; at: string }
  | { type: 'OFFERED'; at: string }
  | { type: 'LOAD'; progress: TourProgress }

export const emptyProgress = (): TourProgress => ({ version: 1, offeredAt: null, tours: {} })

/** The tours a person can take, each with only the steps that apply to them. */
export function toursFor(
  catalog: readonly TourDefinition[],
  access: HelmAccess | null,
): TourDefinition[] {
  if (!access) return []
  return catalog
    .filter((tour) => tour.audience(access))
    .map((tour) => ({ ...tour, steps: tour.steps.filter((step) => step.when?.(access) ?? true) }))
    .filter((tour) => tour.steps.length > 0)
}

function withRecord(progress: TourProgress, tourId: string, patch: Partial<TourRecord>) {
  return {
    ...progress,
    tours: { ...progress.tours, [tourId]: { ...progress.tours[tourId], ...patch } },
  }
}

function clampIndex(index: number, tour: TourDefinition) {
  return Math.min(Math.max(0, Math.trunc(index)), tour.steps.length - 1)
}

function moveTo(state: OnboardingState, tour: TourDefinition, index: number): OnboardingState {
  return {
    active: { tourId: tour.id, index, armed: null },
    progress: withRecord(state.progress, tour.id, { lastStep: index }),
  }
}

function advance(state: OnboardingState, tour: TourDefinition, at: string): OnboardingState {
  const active = state.active!
  if (active.index + 1 < tour.steps.length) return moveTo(state, tour, active.index + 1)
  return {
    active: null,
    progress: withRecord(state.progress, tour.id, { completedAt: at, lastStep: undefined }),
  }
}

export function reduceOnboarding(
  state: OnboardingState,
  event: OnboardingEvent,
  tours: readonly TourDefinition[],
): OnboardingState {
  if (event.type === 'LOAD') return { active: null, progress: event.progress }
  if (event.type === 'OFFERED') {
    return state.progress.offeredAt
      ? state
      : { ...state, progress: { ...state.progress, offeredAt: event.at } }
  }
  if (event.type === 'START') {
    const tour = tours.find((candidate) => candidate.id === event.tourId)
    if (!tour) return state
    return moveTo(state, tour, clampIndex(event.index ?? 0, tour))
  }

  const active = state.active
  const tour = active && tours.find((candidate) => candidate.id === active.tourId)
  if (!active || !tour) return active ? { ...state, active: null } : state

  switch (event.type) {
    case 'NEXT':
      return advance(state, tour, event.at)
    case 'BACK':
      return active.index > 0 ? moveTo(state, tour, active.index - 1) : state
    case 'SKIP':
      return {
        active: null,
        progress: withRecord(state.progress, tour.id, { skippedAt: event.at }),
      }
    case 'OBSERVE': {
      const step = tour.steps[active.index]
      if (!step?.advance || step.id !== event.stepId) return state
      if (active.armed === null) {
        return { ...state, active: { ...active, armed: !event.satisfied } }
      }
      if (active.armed && event.satisfied) return advance(state, tour, event.at)
      if (!active.armed && !event.satisfied) {
        return { ...state, active: { ...active, armed: true } }
      }
      return state
    }
  }
}

export type TourStatus = 'done' | 'started' | 'new'

export function tourStatus(progress: TourProgress, tourId: string): TourStatus {
  const record = progress.tours[tourId]
  if (record?.completedAt) return 'done'
  if (record?.lastStep !== undefined) return 'started'
  return 'new'
}

export function checklistSummary(progress: TourProgress, tours: readonly TourDefinition[]) {
  return {
    done: tours.filter((tour) => tourStatus(progress, tour.id) === 'done').length,
    total: tours.length,
  }
}

/**
 * Getting started opens by itself once, on someone's first sign-in: when they
 * land on their home page (the Dashboard, or the Warehouse for the warehouse
 * team). Someone who followed a link to a record is in the middle of
 * something, so it waits until they come home. Customers are offered it too;
 * theirs holds only the short way around.
 */
export function shouldOfferChecklist(
  progress: TourProgress,
  access: HelmAccess | null,
  tours: readonly TourDefinition[],
  pathname: string,
) {
  if (!access || tours.length === 0 || progress.offeredAt) return false
  const home = access.warehouseOnly ? '/warehouse' : '/'
  return pathname.replace(/(.)\/+$/, '$1') === home
}

/**
 * Where a tour starts: on its first step, unless someone is already further
 * in, on a page one of the lead-in steps would have taken them to.
 */
export function startIndex(tour: TourDefinition, pathname: string) {
  const firstReal = tour.steps.findIndex((step) => !step.lead)
  let start = 0
  for (let index = 0; index <= firstReal; index++) {
    if (tour.steps[index]?.page?.match.test(pathname)) start = index
  }
  return start
}

/** Whether what a step asks for has happened. */
export function isSatisfied(
  advance: TourAdvance,
  where: { pathname: string; present: (target: string) => boolean },
) {
  return 'page' in advance ? advance.page.match.test(where.pathname) : where.present(advance.target)
}

export type StepNote =
  | { kind: 'elsewhere'; label: string; href?: string }
  | { kind: 'missing'; text: string }
  | { kind: 'fallback'; text: string }
  | null

/** What the card says when the step's element is not in view. */
export function stepNote(
  step: TourStep,
  where: { pathname: string; target: 'found' | 'fallback' | 'none' },
): StepNote {
  if (step.page && !step.page.match.test(where.pathname)) {
    return { kind: 'elsewhere', label: step.page.label, href: step.page.href }
  }
  if (where.target === 'fallback' && step.fallback) {
    return { kind: 'fallback', text: step.fallback.note }
  }
  if (where.target === 'none' && step.target && step.missing) {
    return { kind: 'missing', text: step.missing }
  }
  return null
}

/** Reads stored progress, keeping only what has the right shape. */
export function parseProgress(raw: string | null): TourProgress {
  if (!raw) return emptyProgress()
  try {
    const value = JSON.parse(raw) as Partial<TourProgress> | null
    if (!value || value.version !== 1 || typeof value.tours !== 'object' || !value.tours) {
      return emptyProgress()
    }
    const tours: Record<string, TourRecord> = {}
    for (const [id, record] of Object.entries(value.tours)) {
      if (!record || typeof record !== 'object') continue
      const { completedAt, skippedAt, lastStep } = record as TourRecord
      tours[id] = {
        ...(typeof completedAt === 'string' ? { completedAt } : {}),
        ...(typeof skippedAt === 'string' ? { skippedAt } : {}),
        ...(Number.isInteger(lastStep) && lastStep! >= 0 ? { lastStep } : {}),
      }
    }
    return {
      version: 1,
      offeredAt: typeof value.offeredAt === 'string' ? value.offeredAt : null,
      tours,
    }
  } catch {
    return emptyProgress()
  }
}

export type CardSide = 'top' | 'bottom' | 'left' | 'right'

export interface Box {
  top: number
  left: number
  width: number
  height: number
}

const intersects = (a: Box, b: Box) =>
  a.left < b.left + b.width &&
  b.left < a.left + a.width &&
  a.top < b.top + b.height &&
  b.top < a.top + a.height

/**
 * Where the step's card goes. Beside the highlighted element when it fits, on
 * the side the step prefers; otherwise centred. On a phone the card spans the
 * screen and docks to whichever edge leaves the element visible. Either way it
 * keeps clear of `avoid` (the field the person is working in) when it can.
 */
export function placeCard(
  target: Box | null,
  card: { width: number; height: number },
  viewport: { width: number; height: number },
  options: {
    prefer?: CardSide
    gutter?: number
    gap?: number
    phone?: boolean
    avoid?: readonly Box[]
  } = {},
): { top: number; left: number; side: CardSide | 'center' } {
  const gutter = options.gutter ?? 16
  const gap = options.gap ?? 12
  const maxTop = Math.max(gutter, viewport.height - card.height - gutter)
  const maxLeft = Math.max(gutter, viewport.width - card.width - gutter)
  const clamp = (value: number, max: number) => Math.min(Math.max(value, gutter), max)
  const clear = (place: { top: number; left: number }) =>
    !(options.avoid ?? []).some((box) =>
      intersects({ ...place, width: card.width, height: card.height }, box),
    )

  if (options.phone) {
    const bottom = { top: maxTop, left: gutter, side: 'bottom' as const }
    const top = { top: gutter, left: gutter, side: 'top' as const }
    if (!target) return bottom
    const visibleTop = Math.max(target.top, 0)
    const visibleBottom = Math.min(target.top + target.height, viewport.height)
    const middle = (visibleTop + visibleBottom) / 2
    const [first, second] = middle > viewport.height / 2 ? [top, bottom] : [bottom, top]
    return clear(first) || !clear(second) ? first : second
  }

  if (!target) {
    return {
      top: clamp((viewport.height - card.height) / 2, maxTop),
      left: clamp((viewport.width - card.width) / 2, maxLeft),
      side: 'center',
    }
  }

  const right = target.left + target.width
  const below = target.top + target.height
  const places: Record<CardSide, { top: number; left: number; fits: boolean }> = {
    bottom: {
      top: below + gap,
      left: clamp(target.left, maxLeft),
      fits: below + gap + card.height <= viewport.height - gutter,
    },
    top: {
      top: target.top - gap - card.height,
      left: clamp(target.left, maxLeft),
      fits: target.top - gap - card.height >= gutter,
    },
    right: {
      top: clamp(target.top, maxTop),
      left: right + gap,
      fits: right + gap + card.width <= viewport.width - gutter,
    },
    left: {
      top: clamp(target.top, maxTop),
      left: target.left - gap - card.width,
      fits: target.left - gap - card.width >= gutter,
    },
  }
  const order = [options.prefer, 'bottom', 'top', 'right', 'left'].filter(
    (side): side is CardSide => Boolean(side) && places[side as CardSide].fits,
  )
  const side = order.find((candidate) => clear(places[candidate])) ?? order[0]
  if (!side) {
    // Nowhere beside it: the element fills the screen, so sit in its corner.
    return { top: maxTop, left: maxLeft, side: 'center' }
  }
  return { top: places[side].top, left: places[side].left, side }
}
